import type { Easing, Transition, Variants } from 'motion/react';
import { curves, durations, seconds } from '../theme/motion';
import type { NavDirection } from './navigator';
import { PAGE_DURATION_MS, pageEase } from './pageCurves';

// The Cupertino page transition as `motion` variants, shared by pushed pages
// (NavigatorView) and the Menu overlays (ShellLayer). Over 500 ms the page
// slides in from the right with fastEaseInToSlowEaseOut while the route below
// shifts left by a third with linearToEaseOut, and both reverse on Back
// (pageCurves.ts explains why each curve also serves its reverse). With
// reduced motion the page cross-fades in place instead (polish pass).
//
// Desktop layout: pages open inside the content area beside the sidebar, so
// a full-width slide (and the page below shifting a third of the way under
// the sidebar) gives way to a short fade with a 24 px slide; the page below
// stays put.

const PAGE_DURATION_S = PAGE_DURATION_MS / 1000;
const PARALLAX = '-33.333%';

/** Reduced motion: the length of the cross-fade that replaces the slide. */
export const REDUCED_FADE_MS = durations.quick;

/** Desktop layout: the length of the page fade (and slide). */
export const DESKTOP_PAGE_MS = durations.standard;
const DESKTOP_SHIFT_PX = 24;

/** How long the layer below keeps painting once a page starts covering it. */
export function coverDelayMs(reduced: boolean, desktop = false): number {
  return reduced ? REDUCED_FADE_MS : desktop ? DESKTOP_PAGE_MS : PAGE_DURATION_MS;
}

/** Base or page `custom` value. */
export interface LayerMotion {
  direction: NavDirection;
  reduced: boolean;
  /** Desktop layout: the short fade instead of the Cupertino slide. */
  desktop?: boolean;
}

const fade: Transition = { duration: seconds.quick, ease: curves.easeOut };
const parallaxEase: Easing = [...curves.linearToEaseOut];

function slide(ease: Easing): Transition {
  return { duration: PAGE_DURATION_S, ease };
}

const desktopIn: Transition = { duration: DESKTOP_PAGE_MS / 1000, ease: curves.easeOutCubic };
const desktopOut: Transition = { duration: seconds.quick, ease: curves.easeInCubic };

/** Popped pages slide out to the right; a page replaced by a push shifts left underneath. */
export function pageExit({ direction, reduced, desktop }: LayerMotion) {
  if (desktop && !reduced) {
    // A replaced page stays until the new one has faded in over it; a popped page fades out to the right.
    return direction === 'forward'
      ? { opacity: 0, transition: { duration: 0, delay: DESKTOP_PAGE_MS / 1000 } }
      : { x: DESKTOP_SHIFT_PX, opacity: 0, transition: desktopOut };
  }
  if (reduced) {
    // A replaced page stays opaque until the new one has faded in over it.
    return direction === 'forward' ? { opacity: 0, transition: { duration: 0, delay: seconds.quick } } : { opacity: 0, transition: fade };
  }
  return direction === 'forward' ? { x: PARALLAX, transition: slide(parallaxEase) } : { x: '100%', transition: slide(pageEase) };
}

export const layerVariants: Variants = {
  enter: ({ direction, reduced, desktop }: LayerMotion) =>
    reduced
      ? { x: '0%', opacity: direction === 'forward' ? 0 : 1 }
      : desktop
        ? { x: direction === 'forward' ? DESKTOP_SHIFT_PX : 0, opacity: direction === 'forward' ? 0 : 1 }
        : { x: direction === 'back' ? PARALLAX : direction === 'none' ? '0%' : '100%' },
  // A page becoming active on a push is the incoming page; on Back it is the one uncovered (parallax).
  active: ({ direction, reduced, desktop }: LayerMotion) =>
    reduced
      ? { x: '0%', opacity: 1, transition: fade }
      : desktop
        ? { x: 0, opacity: 1, transition: desktopIn }
        : { x: '0%', opacity: 1, transition: slide(direction === 'forward' ? pageEase : parallaxEase) },
  covered: ({ reduced, desktop }: LayerMotion) =>
    reduced || desktop ? { x: 0, opacity: 1, transition: { duration: 0 } } : { x: PARALLAX, transition: slide(parallaxEase) },
  exit: (motionState: LayerMotion) => pageExit(motionState),
};
